// Service that we use to wrap segment's implementation.
// @flow
import { SEGMENT_EVENTS } from './SegmentEvents';
import sendToTelemetry from './sendToTelemetry';

export const EVENTS = SEGMENT_EVENTS;

const SEND_TO_SEGMENT = true;

type User = {
    id: number,
    email: string
};

type TrackingCallback = (...any) => void | null;
type EventProperties = any;
type EventOptions = any;
type ParsedParams = any;
type ParamParser = (...any) => ParsedParams | null;

export default class SegmentService {
    static params: any = {};
    static paramParser: ParamParser = null;

    // To capture parameters into the data collected by
    // segment, you can provide a parameter parser using this function.
    //
    // On navigation change, the parser is called and the expected output
    // is an object containing key/value pairs which correspond to query string
    // and/or route parameters.
    //
    // The API for the parameter parser function needs to match what is passed
    // into the page method, because the parameters sent to that method are
    // forwarded onto the parser.
    //
    // Why? Each application may have different data that needs to be sent to
    // the parser, this provides the flexibility needed to accommodate that
    // need.
    //
    // The return value needs to be an object.
    static setParamParser(parser: ParamParser) {
        SegmentService.paramParser = parser;
    }

    // Options are in reference to the segment API 'options'. This serves as
    // a dictionary of options to be tacked on to the segment API call. We use
    // this to decorate the context of that call with the parameters which
    // exist for the current page that is being visited.
    static get options() {
        return {
            context: {
                URL_params: SegmentService.params
            }
        };
    }

    // Identify wraps segment's identify API.
    // NOTE: We tack on the current page params in the payload.
    static identify(loggedInUser: User): void {
        window.analytics.identify(
            loggedInUser.id,
            {
                email: loggedInUser.email
            },
            SegmentService.options
        );
    }

    // Track wraps segment's track API.
    // Only valid events are sent, which returns true. If an invalid
    // event is passed, the method returns false and does not sent the event.
    //
    // NOTE: We tack on the current page params in the payload.
    static track(
        eventName: string,
        properties: EventProperties = {},
        options: EventOptions = {},
        callback: TrackingCallback = null
    ): boolean {
        const isValidEvent = isValidSegmentEvent(eventName, properties);

        if (!isValidEvent) {
            console.error('Invalid Segment Event', eventName);
            return false;
        }

        // Pass the options provided through, and decorate those options
        // with the context we want to have set.
        const optionsWithParams = {
            ...options,
            ...SegmentService.options
        };

        window.analytics.track(
            eventName,
            properties,
            optionsWithParams,
            callback
        );
        return true;
    }

    static trackToApi(
        eventName: string,
        properties: EventProperties = {}
    ): boolean {
        try {
            sendToTelemetry(eventName, properties, SEND_TO_SEGMENT);
        } catch (e) {
            return false;
        }
        return true;
    }

    // Page wraps segment's page API.
    // Along with the page change, we send the parameters detected.
    static page(...args: any): void {
        if (typeof SegmentService.paramParser === 'function') {
            SegmentService.params = SegmentService.paramParser(...args);
        }

        // Do not pass the category, or the name.
        // Use the defaulted properties list.
        // Provide the params to the context via the options object.
        // https://segment.com/docs/sources/website/analytics.js/#page
        window.analytics.page(undefined, undefined, {}, SegmentService.options);
    }
}

// Checks to see if events being sent are in the dictionary of events.
export const isValidSegmentEvent = (
    eventName: string,
    passedProperties: EventProperties
): boolean => {
    if (!SEGMENT_EVENTS.hasOwnProperty(eventName)) {
        return false;
    }

    const requiredProps = SEGMENT_EVENTS[eventName].properties;
    const hasRequiredProperties = requiredProps.every(
        (requiredProp: string) => requiredProp in passedProperties
    );

    return hasRequiredProperties;
};



// WEBPACK FOOTER //
// ./src/lib/segment/SegmentService.js